/** One recent-search row (Home panel and full page). `to` is precomputed by the parent; null = not replayable. */
import React from 'react';
import type { SearchHistoryEntry } from '../../types';
import { describeSearch, regionSummary } from '../../utils/describeSearch';
import { ShellNavLink } from './ShellNavLink';

interface Props {
  entry: SearchHistoryEntry;
  to: string | null;
  replace?: boolean | undefined;
  trailing?: React.ReactNode;
  meta?: React.ReactNode;
}

function tagFor(entry: SearchHistoryEntry): string {
  if (entry.mode === 'main') return 'COGNI';
  if (entry.mode === 'keyword') return 'KEYWORD';
  if (entry.mode === 'letter') return 'LETTER';
  return describeSearch(entry).modeLabel.toUpperCase();
}

export const SearchHistoryRow: React.FC<Props> = ({ entry, to, replace, trailing, meta }) => {
  const described = describeSearch(entry);
  const count = entry.result_count != null ? `${entry.result_count} results` : null;
  const metaText = [count, regionSummary((entry.payload ?? {}) as Record<string, unknown>)].filter(Boolean).join(' · ');
  const body = (
    <>
      <span className="font-mono text-[11px] text-accent">{tagFor(entry)}</span>
      <span className={`text-[14px] leading-[1.4] ${to === null ? 'text-ink-3' : 'text-ink'}`}>{described.text}</span>
      <span className="text-[12px] text-ink-3">
        {metaText}
        {meta != null && <>{metaText ? ' · ' : ''}{meta}</>}
      </span>
    </>
  );
  return (
    <div className="flex items-start gap-2 border-b border-line-subtle last:border-b-0" data-testid={`recent-row-${entry.id}`}>
      {to === null ? (
        <div aria-disabled="true" className="flex min-w-0 flex-1 flex-col gap-1 py-2.5">{body}</div>
      ) : (
        <ShellNavLink to={to} replace={replace} className="flex min-w-0 flex-1 flex-col gap-1 py-2.5 text-ink no-underline hover:text-accent">
          {body}
        </ShellNavLink>
      )}
      {trailing != null && <div className="shrink-0 py-2.5">{trailing}</div>}
    </div>
  );
};

export default SearchHistoryRow;
